import { Button, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { DestinationPlate } from "features/WorklistTools/shared/components/DestinationPlate";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { PlateBarcodeField } from "features/WorklistTools/shared/components/PlateBarcodeField";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import {
  PlatePosition,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import { is96WPlate } from "features/WorklistTools/shared/PlateHelpers";
import { setDestPlateType } from "features/WorklistTools/shared/WorklistHelpers";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import { SelectableGroup } from "react-selectable-fast";
import { ConfirmDialog } from "shared-components/ModalsAndDialogs/ConfirmDialog";
import { Alert, AlertType } from "shared-components/toast";
import { useConfirm } from "state-provider/ConfirmDialogProvider/hooks";
import styled from "styled-components";
import { twelve24WBefore96WDest } from "../NucleofectionToolHelpers";
import {
  DestPlateInfoState,
  DestPlateStringKeys,
  IntermediatePlateInfoState,
  NucleofectionDestPlateLabwareTypeInfo,
  NucleofectionPlateInfoState,
  NucleofectionToolActions,
} from "../state";
import { useNucleofectionStateProvider } from "../state/StateProvider";
import { selectDestWells } from "./handlers/SelectDestinationWells";

const StyledSelectableGroup = styled(SelectableGroup)`
  height: 320px;
  width: 100%; //425px;;
`;

const StyledPlateSettingsContainer = styled.div`
  margin-top: 10px;
  width: 100%;
`;

const StyledPlateActions = styled.div`
  width: 100%;
  display: flex;
  margin-top: 7px;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 10px;
`;

export const DestinationPlatesContainer = (destPlateTypeInfo: any) => {
  const { isConfirmed } = useConfirm();
  const { nucleofectionInternalState } = useNucleofectionStateProvider();
  const dispatch = useAppReduxDispatch();
  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.sourcePlateInfo
  );
  const destPlateInfo: DestPlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.destPlateInfo
  );
  const intPlateInfo: IntermediatePlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.intPlateInfo
  );

  const nucPlateInfo: NucleofectionPlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.nucPlateInfo
  );

  const worklistValues = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.worklistValues
  );

  const {
    methodSettings: { destTransferStrategy },
  } = nucleofectionInternalState;

  const handleDestinationWellSelection = (destSelection: any[]) => {
    selectDestWells(
      nucleofectionInternalState,
      destPlateInfo,
      worklistValues,
      dispatch,
      destSelection,
      isConfirmed
    );
  };

  const handlePlateTypeChange = (
    index: number,
    plateTypeInfo: NucleofectionDestPlateLabwareTypeInfo
  ) => {
    if (nucPlateInfo[0].plateBarcode === "") {
      Alert({
        type: AlertType.ERROR,
        message:
          "Missing Nucleofection plate barcode. Set Nucleofection plate and reselect Destination plate type.",
      });
      return;
    }

    plateTypeInfo = setDestPlateType(plateTypeInfo);

    if (twelve24WBefore96WDest(plateTypeInfo, index, destPlateInfo)) {
      Alert({
        type: AlertType.ERROR,
        message:
          "Please add all 96-Well Destination plates before 12/24-Well plates",
      });
      return;
    }
    // set the plate type first, but clear the barcode
    dispatch(
      NucleofectionToolActions.SET_DESTINATION_PLATE({
        index,
        rows: plateTypeInfo.plateRows,
        cols: plateTypeInfo.plateCols,
        operatingVol: plateTypeInfo.defaultLabwareVolume,
        minOperatingVol: plateTypeInfo.minLabwareVolume,
        labwareTypeCode: plateTypeInfo.labwareTypeCode,
        plateBarcode: "",
        topup: plateTypeInfo?.topup ?? true,
        preprocess: plateTypeInfo?.preprocess ?? true,
      })
    );
    if (
      is96WPlate(
        plateTypeInfo.plateRows?.length ?? 0,
        plateTypeInfo.plateCols?.length ?? 0
      )
    ) {
      const intToDestMapping = worklistValues.nucToInt.map((item) => ({
        sourcePlateIndex: 0, // intermediate plate index
        sourceWellId: item.destWellId, // well from intermediate plate
        destPlateIndex: index,
        destWellId: item.destWellId, // same well position in destination (stamp)
        transferVol: "",
      }));
      dispatch(
        NucleofectionToolActions.INT_TO_DEST_ADDED({
          selections: intToDestMapping,
        })
      );
    }
  };

  const handleDestPlateSettingsChanged = (
    index: number,
    key: DestPlateStringKeys,
    value: string | number | boolean
  ) => {
    dispatch(
      NucleofectionToolActions.UPDATE_DEST_PLATE_INFO({
        index,
        key,
        value,
      })
    );
  };

  const handleAddDestinationPlateClick = () => {
    dispatch(NucleofectionToolActions.ADD_DESTINATION_PLATE({}));
  };

  const handleRemovePlateClick = (index: number) => {
    dispatch(
      NucleofectionToolActions.CLEAR_DESTINATION_PLATE({
        index,
      })
    );
  };

  return (
    <div
      style={{
        height: "calc(100vh - 300px)",
        width: "600px",
        overflow: "auto",
      }}
    >
      {destPlateInfo.map((item: DestPlateInfoState, index: number) => (
        <div style={{ padding: "20px", width: "100%" }}>
          <PlateBarcodeField
            plateRole={PlatePosition.Destination}
            index={index}
            label={"Destination Plate Barcode"}
            plateBarcode={item.plateBarcode}
            handlePlateBarcodeChange={(index, field, value) =>
              handleDestPlateSettingsChanged(index!, field, value)
            }
          />
          <StyledSelectableGroup
            onSelectionFinish={
              item.labwareTypeCode.includes("_12_") ||
              item.labwareTypeCode.includes("_24_")
                ? handleDestinationWellSelection
                : () => {
                    return;
                  }
            }
            resetOnStart={true}
          >
            <DestinationPlate
              worklistTool={WorklistTools.Nucleofection}
              index={index}
              plateRows={item.rows}
              plateCols={item.cols}
              wellMapping={worklistValues.intToDest}
              sourcePlatePosition={PlatePosition.Intermediate}
              sourcePlateInfo={sourcePlateInfo}
            />
          </StyledSelectableGroup>
          <StyledPlateActions>
            <Button
              variant="outlined"
              disabled={item.rows.length < 1}
              onClick={() => handleRemovePlateClick(index)}
            >
              Remove Plate
            </Button>
          </StyledPlateActions>
          <StyledPlateSettingsContainer>
            <PlateType
              platePosition={PlatePosition.Destination}
              isDisabled={
                (index > 0 && destPlateInfo[index - 1].rows.length === 0) ||
                nucleofectionInternalState.methodSettings!.selectedSystem ===
                  0 ||
                sourcePlateInfo[0].rows.length === 0 ||
                destPlateInfo[index].rows.length > 0
              }
              acceptedPlateTypes={destPlateTypeInfo.destPlateInfo}
              plateType={item.labwareTypeCode}
              setPlateType={(plateTypeInfo) =>
                handlePlateTypeChange(index, plateTypeInfo)
              }
            />
            <div style={{ display: "flex", width: "100%" }}>
              <NumberField
                style={{ width: "50%", marginTop: "10px", marginRight: "5px" }}
                id="operating-vol"
                label="Operating Vol (µL)"
                value={destPlateInfo[index].operatingVol}
                onChange={(value) =>
                  handleDestPlateSettingsChanged(
                    index,
                    "operatingVol",
                    parseInt(value)
                  )
                }
                disabled={true}
              />
              <NumberField
                style={{
                  width: "50%",
                  marginTop: "10px",
                  marginRight: "5px",
                }}
                id="preprocess-min-vol"
                label="PreProcess Min Vol (µL)"
                value={item.minOperatingVol}
                onChange={(value) =>
                  handleDestPlateSettingsChanged(
                    index,
                    "minOperatingVol",
                    parseInt(value)
                  )
                }
                disabled={true}
              />
            </div>
            <FormGroup sx={{ marginTop: "15px", alignItems: "flex-start" }} row>
              <FormControlLabel
                control={
                  <Checkbox
                    name="Topup"
                    checked={destPlateInfo[index].topup}
                    onChange={(e) =>
                      handleDestPlateSettingsChanged(
                        index,
                        "topup",
                        e.target.checked
                      )
                    }
                  />
                }
                label="Topup"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="Preprocess Plate"
                    checked={destPlateInfo[index].preprocess}
                    onChange={(e) => {
                      handleDestPlateSettingsChanged(
                        index,
                        "preprocess",
                        e.target.checked
                      );
                    }}
                  />
                }
                label="Preprocess Plate"
              />
            </FormGroup>
          </StyledPlateSettingsContainer>
        </div>
      ))}

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="outlined"
          onClick={() => handleAddDestinationPlateClick()}
          disabled={destPlateInfo.length === 4}
        >
          Add Plate
        </Button>
      </div>
      <ConfirmDialog />
    </div>
  );
};
